/**
 * sunTimes.ts — sunset for a place on a date, worked out on the device (no
 * API call). Accurate to a minute or two, which is all a "Sunset 7:41 pm"
 * line on a day header needs.
 *
 * Uses the standard almanac method (zenith 90.833°: the sun's upper edge on
 * the horizon, refraction included). The clock is written in the place's own
 * time zone, so a Perth night reads in Perth time even when you plan from
 * Melbourne.
 */

const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;
const mod = (n: number, m: number) => ((n % m) + m) % m;

/** Sunset as a UTC Date for a YYYY-MM-DD at lat/lng, or null (polar day/night). */
export function sunsetUtc(lat: number, lng: number, iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const start = Date.UTC(y, 0, 1);
  const dayOfYear = Math.floor((Date.UTC(y, m - 1, d) - start) / 86400000) + 1;
  const lngHour = lng / 15;
  const t = dayOfYear + (18 - lngHour) / 24;
  const meanAnomaly = 0.9856 * t - 3.289;
  const trueLng = mod(meanAnomaly + 1.916 * Math.sin(rad(meanAnomaly)) + 0.02 * Math.sin(rad(2 * meanAnomaly)) + 282.634, 360);
  let ra = mod(deg(Math.atan(0.91764 * Math.tan(rad(trueLng)))), 360);
  ra = (ra + Math.floor(trueLng / 90) * 90 - Math.floor(ra / 90) * 90) / 15;
  const sinDec = 0.39782 * Math.sin(rad(trueLng));
  const cosDec = Math.cos(Math.asin(sinDec));
  const cosH = (Math.cos(rad(90.833)) - sinDec * Math.sin(rad(lat))) / (cosDec * Math.cos(rad(lat)));
  if (cosH > 1 || cosH < -1) return null;
  const hourAngle = deg(Math.acos(cosH)) / 15;
  const localMean = hourAngle + ra - 0.06571 * t - 6.622;
  const ut = mod(localMean - lngHour, 24);
  return new Date(Date.UTC(y, m - 1, d) + ut * 3600000);
}

/**
 * The IANA zone for a point in Australia, from rough state lines. Outside
 * Australia (EscapeTo's other countries) it returns undefined and the
 * browser's own zone is used.
 */
export function auTimeZone(lat: number, lng: number): string | undefined {
  if (lat > -9 || lat < -44.5 || lng < 112 || lng > 154.5) return undefined;
  if (lng < 129) return 'Australia/Perth';
  if (lng < 138) return lat > -26 ? 'Australia/Darwin' : 'Australia/Adelaide';
  if (lng < 141) return lat > -26 ? 'Australia/Brisbane' : 'Australia/Adelaide';
  // Queensland keeps standard time all year; its southern border is ~29°S.
  if (lat > -28.9) return 'Australia/Brisbane';
  return lat < -39.5 ? 'Australia/Hobart' : 'Australia/Sydney';
}

/** "7:41 pm" — sunset at a place on a date, in that place's time. */
export function sunsetLabel(lat?: number | null, lng?: number | null, iso?: string | null): string | null {
  if (typeof lat !== 'number' || typeof lng !== 'number' || !iso) return null;
  const at = sunsetUtc(lat, lng, iso);
  if (!at) return null;
  try {
    return at
      .toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone: auTimeZone(lat, lng) })
      .replace(/\s?([AP])M$/i, (_, p: string) => ` ${p.toLowerCase()}m`)
      .replace(/ /g, ' ');
  } catch {
    return null;
  }
}
